import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u6_x1_rauchmelder';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: 'Einführung',
    body: 'Kanalrauchmelder wie der Oppermann KRM-X-2 detektieren frühzeitig Rauch in Lüftungs- und Klimakanälen. Ihre Hauptaufgabe ist es, die Rauchweiterleitung zu verhindern, indem sie z. B. Lüftungsanlagen abschalten und Brandschutzklappen schließen. Sie arbeiten meist nach dem optischen Streulichtprinzip.'
  },
  {
    heading: 'Oppermann KRM-X-2 (Version 0.16) im Detail',
    body: 'Der KRM-X-2 (hier Version 0.16 als Beispiel) ist ein optischer Kanalrauchmelder zur Früherkennung von Schwelbränden und Bränden mit Rauchentwicklung. Er verfügt über potentialfreie Relaiskontakte für Alarm und ggf. Störung. Wichtige Merkmale (Details dem Datenblatt der spezifischen Version entnehmen):\n\n• Spannungsversorgung: typ. 24V AC/DC.\n• Alarmrelais: Wechsler (COM/NO/NC) zur Anbindung an DDC/SPS oder BMZ.\n• LED-Anzeigen: Für Betrieb, Alarm, Störung.\n• Messrohr: Zur Luftentnahme aus dem Kanal.\n• Regelmäßige Wartung und Prüfung sind vorgeschrieben und essentiell.'
  },
  {
    heading: 'Funktion und Maßnahmen im Alarmfall',
    body: 'Funktionsprinzip (Streulicht): Rauchpartikel in der Messkammer streuen das Licht einer LED, welches von einem Sensor detektiert wird. Bei Überschreitung eines Schwellenwerts wird Alarm ausgelöst.\n\nTypische Maßnahmen bei Alarm:\n• Lüftungsanlage AUS.\n• Brandschutzklappen ZU.\n• Alarmierung (lokal und/oder an Leitstelle/BMZ).\n• Ggf. Ansteuerung von Entrauchungseinrichtungen.\n\nAnschluss: Der Alarmkontakt wird an einen digitalen Eingang (DI) der Gebäudeautomation oder direkt an eine Brandmeldeanlage angeschlossen.'
  },
  {
    heading: 'Wartung und Prüfung',
    body: 'Kanalrauchmelder sind sicherheitsrelevante Bauteile. Eine regelmäßige Inspektion, Wartung und Prüfung gemäß Herstellervorgaben und geltenden Normen (z. B. Prüfung mit Prüfaerosol, Reinigung) ist zwingend erforderlich, um die Funktionssicherheit zu gewährleisten.'
  },
  {
    heading: 'Fazit',
    body: 'Ein korrekt installierter, funktionierender und regelmäßig gewarteter Kanalrauchmelder wie der Oppermann KRM‑X‑2 ist ein unverzichtbarer Bestandteil des anlagentechnischen Brandschutzes.'
  }
];

const images = [
  { id: 'img_rm_krmx2_beispiel', label: 'KRM‑X‑2 Gerät', src: `${IMAGE_BASE}sicherheit_oppermann_krm.jpg`, alt: 'Kanalrauchmelder Oppermann KRM‑X‑2' },
  { id: 'img_rm_montage_prinzip', label: 'Montage / Anschluss', src: `${IMAGE_BASE}oppermann_krm_anschluss.PNG`, alt: 'Montageprinzip und Anschluss des KRM‑X‑2' },
  { id: 'img_rm_montage_prinzip2', label: 'Schaltplan', src: `${IMAGE_BASE}schaltplan_rauchmelder.PNG`, alt: 'Schaltplan Kanalrauchmelder' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.6.1 Rauchmelder', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, { key: img.id, src: imgSrc(img.id, img.src), alt: img.alt, label: img.label, onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) => React.createElement(EditableTextBlock, { key: i, pageId: PAGE_ID, blockId: `block_${i}`, heading: block.heading, body: block.body, onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody) })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } }, React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u6_x99_quiz') }, 'Zum Quiz'))
      )
    )
  );
};

export default Page;


